/**
 * BudgetTab - Capital Expenditure Configuration
 *
 * Allows users to configure CapEx before running optimization.
 * Extracted from FinancialAnalysis.js CAPEX tab.
 */

import React, { useEffect } from 'react';
import {
    Box, Card, CardContent, Typography, Table, TableHead, TableBody,
    TableRow, TableCell, TextField, Chip, Alert, Grid
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { vortxColors, formatCurrency, formatNumber, useEquipmentDatabase } from '@vortx/shared-ui';

// Format input number with commas
function formatInputNumber(value) {
    if (!value || value === '') return '';
    const num = parseFloat(value.toString().replace(/,/g, ''));
    if (isNaN(num)) return '';
    return num.toLocaleString('en-US');
}

/**
 * BudgetTab Props:
 * @param {Object} systemSizes - { pvKw, batteryKw, batteryKwh, generatorKw }
 * @param {Object} budget - Budget state from useBudget hook
 * @param {boolean} hasSystemConfigured - Whether any DER is configured in Microgrid Design tab
 * @param {Array} generators - Array of generator objects from Microgrid Design tab
 * @param {Object} selectedSolarData - Selected solar equipment
 * @param {Function} setSelectedSolarData - Function to update selected solar equipment
 * @param {Object} selectedBatteryData - Selected battery equipment
 * @param {Function} setSelectedBatteryData - Function to update selected battery equipment
 * @param {Object} selectedGeneratorData - Selected generator equipment
 * @param {Function} setSelectedGeneratorData - Function to update selected generator equipment
 * @param {Object} selectedSwitchgearData - Selected switchgear equipment
 * @param {Function} setSelectedSwitchgearData - Function to update selected switchgear equipment
 */
function BudgetTab({
    systemSizes = {},
    budget,
    hasSystemConfigured = false,
    readOnly = false,
    generators = [],
    selectedSolarData,
    setSelectedSolarData,
    selectedBatteryData,
    setSelectedBatteryData,
    selectedGeneratorData,
    setSelectedGeneratorData,
    selectedSwitchgearData,
    setSelectedSwitchgearData
}) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    // Fetch equipment from database
    const { data: solarEquipment = [], loading: loadingSolar } = useEquipmentDatabase('solar');
    const { data: batteryEquipment = [], loading: loadingBattery } = useEquipmentDatabase('bess');
    const { data: generatorEquipment = [], loading: loadingGenerator } = useEquipmentDatabase('generator');
    const { data: switchgearEquipment = [], loading: loadingSwitchgear } = useEquipmentDatabase('switchgear');

    console.log('🔧 [BudgetTab] Equipment loaded:', {
        solar: solarEquipment.length,
        battery: batteryEquipment.length,
        generator: generatorEquipment.length,
        switchgear: switchgearEquipment.length,
        loadingSolar,
        loadingBattery,
        loadingGenerator,
        loadingSwitchgear
    });

    const {
        capexInputs,
        itcInputs,
        includeItc,
        setIncludeItc,
        capexSource,
        setCapexSource,
        capexCalculations,
        itcCalculations,
        updateCapexInput,
        updateInstallationCost,
        updateSoftCost,
        updateItcInput,
        loading
    } = budget;

    const { pvKw = 0, batteryKw = 0, batteryKwh = 0, generatorKw = 0 } = systemSizes;

    // Restore equipment selections from session storage (set during scenario load)
    useEffect(() => {
        const pendingEquipmentStr = window.sessionStorage.getItem('pendingEquipmentLoad');
        if (pendingEquipmentStr && solarEquipment.length > 0) {
            try {
                const equipmentIds = JSON.parse(pendingEquipmentStr);
                console.log('🔧 [BudgetTab] Restoring equipment selections:', equipmentIds);

                // Find and set solar equipment
                if (equipmentIds.solar && !selectedSolarData) {
                    const solar = solarEquipment.find(eq => eq.id === equipmentIds.solar);
                    if (solar) {
                        setSelectedSolarData(solar);
                        console.log('✅ Restored solar equipment:', solar.manufacturer, solar.model);
                    }
                }

                // Find and set battery equipment (supports array [{id, qty}] and legacy single ID)
                if (equipmentIds.battery && !selectedBatteryData) {
                    if (Array.isArray(equipmentIds.battery)) {
                        // Multi-battery format: [{id, qty}, ...]
                        const restored = equipmentIds.battery.map(entry => {
                            const dbBatt = batteryEquipment.find(eq => eq.id === entry.id);
                            if (!dbBatt) return null;
                            const kwh = dbBatt.capacity_kwh || 0;
                            return {
                                id: dbBatt.id,
                                manufacturer: dbBatt.manufacturer,
                                model: dbBatt.model,
                                kw: dbBatt.power_kw,
                                kwh,
                                qty: entry.qty || 1,
                                cost_per_kwh: dbBatt.cost_per_kwh ?? (dbBatt.equipment_cost && kwh ? dbBatt.equipment_cost / kwh : 0),
                                cost_per_kw: dbBatt.cost_per_kw ?? (dbBatt.equipment_cost && dbBatt.power_kw ? dbBatt.equipment_cost / dbBatt.power_kw : 0),
                                install_cost_per_kwh: dbBatt.install_cost_per_kwh ?? (dbBatt.installation_cost && kwh ? dbBatt.installation_cost / kwh : 0),
                                equipment_cost: dbBatt.equipment_cost,
                                installation_cost: dbBatt.installation_cost,
                            };
                        }).filter(Boolean);
                        if (restored.length > 0) {
                            setSelectedBatteryData(restored);
                            console.log('✅ Restored battery equipment (multi):', restored.map(b => `${b.manufacturer} ${b.model} ×${b.qty}`));
                        }
                    } else {
                        // Legacy single-ID format
                        const battery = batteryEquipment.find(eq => eq.id === equipmentIds.battery);
                        if (battery) {
                            setSelectedBatteryData(battery);
                            console.log('✅ Restored battery equipment:', battery.manufacturer, battery.model);
                        }
                    }
                }

                // Find and set generator equipment
                if (equipmentIds.generator && !selectedGeneratorData) {
                    const generator = generatorEquipment.find(eq => eq.id === equipmentIds.generator);
                    if (generator) {
                        setSelectedGeneratorData(generator);
                        console.log('✅ Restored generator equipment:', generator.manufacturer, generator.model);
                    }
                }

                // Find and set switchgear equipment
                if (equipmentIds.switchgear && !selectedSwitchgearData) {
                    const switchgear = switchgearEquipment.find(eq => eq.id === equipmentIds.switchgear);
                    if (switchgear) {
                        setSelectedSwitchgearData(switchgear);
                        console.log('✅ Restored switchgear equipment:', switchgear.manufacturer, switchgear.model);
                    }
                }

                // Clear pending load after processing
                window.sessionStorage.removeItem('pendingEquipmentLoad');
            } catch (error) {
                console.error('Failed to parse pending equipment IDs:', error);
                window.sessionStorage.removeItem('pendingEquipmentLoad');
            }
        }
    }, [solarEquipment, batteryEquipment, generatorEquipment, switchgearEquipment, selectedSolarData, selectedBatteryData, selectedGeneratorData, selectedSwitchgearData, setSelectedSolarData, setSelectedBatteryData, setSelectedGeneratorData, setSelectedSwitchgearData]);

    // Auto-populate equipment selections from Microgrid Design tab
    useEffect(() => {
        // Only auto-populate if equipment database is loaded
        if (generatorEquipment.length === 0) return;

        // Auto-select generator equipment based on first generator with database_id
        if (generators.length > 0 && !selectedGeneratorData) {
            const firstGenWithDb = generators.find(g => g.database_id);
            if (firstGenWithDb?.database_id) {
                const dbGen = generatorEquipment.find(eq => eq.id === firstGenWithDb.database_id);
                if (dbGen) {
                    setSelectedGeneratorData(dbGen);
                    console.log('✅ [BudgetTab] Auto-populated generator equipment from Microgrid Design:', dbGen.manufacturer, dbGen.model);
                }
            }
        }
    }, [generators, generatorEquipment, selectedGeneratorData, setSelectedGeneratorData]);

    // Show empty state if no system configured
    if (!hasSystemConfigured) {
        return (
            <Card>
                <CardContent>
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                        <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 600, mb: 2 }}>
                            Configure Your Microgrid First
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Go to the <strong>Microgrid Design</strong> tab to configure your DER system (PV, Battery, Generator),
                            then return here to set up your budget.
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    if (loading) {
        return (
            <Card>
                <CardContent>
                    <Typography>Loading pricing defaults...</Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Box>
            {/* Header with toggles - hidden in readOnly mode */}
            {!readOnly && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Grid container spacing={3} alignItems="center">
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Capital Expenditure Budget
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Configure equipment costs before running optimization
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    Federal Incentives (ITC)
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Chip
                                        label="Include ITC"
                                        onClick={() => setIncludeItc(true)}
                                        color={includeItc ? 'success' : 'default'}
                                        variant={includeItc ? 'filled' : 'outlined'}
                                        size="small"
                                        sx={{ cursor: 'pointer' }}
                                    />
                                    <Chip
                                        label="Exclude ITC"
                                        onClick={() => setIncludeItc(false)}
                                        color={!includeItc ? 'warning' : 'default'}
                                        variant={!includeItc ? 'filled' : 'outlined'}
                                        size="small"
                                        sx={{ cursor: 'pointer' }}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            )}

            {/* Info message for readOnly mode */}
            {readOnly && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    This is a read-only view of your Budget. To edit CapEx values, go to the <strong>Budget</strong> tab.
                </Alert>
            )}

            {/* Consolidated CAPEX Table */}
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>Capital Expenditure Breakdown</Typography>
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small" sx={{ minWidth: 1000 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ minWidth: 180 }}>Component</TableCell>
                                    <TableCell align="center" sx={{ minWidth: 120 }}>Size</TableCell>
                                    <TableCell align="center" sx={{ minWidth: 100 }}>Rate</TableCell>
                                    <TableCell align="center" sx={{ minWidth: 120 }}>Lump Sum</TableCell>
                                    <TableCell align="right" sx={{ minWidth: 100 }}>Upfront Cost</TableCell>
                                    <TableCell align="center" sx={{ minWidth: 80 }}>ITC %</TableCell>
                                    <TableCell align="right" sx={{ minWidth: 100 }}>ITC Amount</TableCell>
                                    <TableCell align="right" sx={{ minWidth: 100, fontWeight: 600 }}>Net Cost</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {/* TURNKEY SYSTEM COSTS */}
                                <TableRow sx={{ backgroundColor: isDark ? 'rgba(100, 150, 200, 0.1)' : '#f0f8ff' }}>
                                    <TableCell colSpan={8}><strong>TURNKEY SYSTEM COSTS</strong></TableCell>
                                </TableRow>
                                {pvKw > 0 && (
                                    <TableRow>
                                        <TableCell sx={{ pl: 3 }}>Solar PV (Turnkey)</TableCell>
                                        <TableCell align="center">{formatNumber(pvKw)} kW</TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                <TextField
                                                    size="small"
                                                    value={formatInputNumber(capexInputs.pvTurnkey?.rate)}
                                                    onChange={(e) => updateCapexInput('pvTurnkey', 'rate', e.target.value)}
                                                    sx={{ width: 80 }}
                                                    InputProps={{ startAdornment: '$' }}
                                                    disabled={readOnly}
                                                />
                                                <Typography variant="body2">/kW</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <TextField
                                                size="small"
                                                value={formatInputNumber(capexInputs.pvTurnkey?.totalCost || 0)}
                                                onChange={(e) => updateCapexInput('pvTurnkey', 'totalCost', e.target.value)}
                                                sx={{ width: 100 }}
                                                InputProps={{ startAdornment: '$' }}
                                                disabled={readOnly}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" fontWeight="medium">
                                                {formatCurrency(capexCalculations.components?.pvTurnkey?.total || 0)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    value={itcInputs.pvTurnkey || ''}
                                                    onChange={(e) => updateItcInput('pvTurnkey', e.target.value)}
                                                    sx={{ width: 80 }}
                                                    inputProps={{ style: { textAlign: 'right' } }}
                                                    disabled={readOnly || !includeItc}
                                                />
                                                <Typography variant="body2" sx={{ ml: 0.5 }}>%</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" color="success.main">
                                                -{formatCurrency(itcCalculations.pvTurnkey?.itcAmount || 0)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" fontWeight="bold">
                                                {formatCurrency(itcCalculations.pvTurnkey?.netCost || 0)}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}

                                {/* EQUIPMENT COSTS */}
                                <TableRow sx={{ backgroundColor: isDark ? 'rgba(100, 150, 200, 0.1)' : '#f0f8ff' }}>
                                    <TableCell colSpan={8}><strong>EQUIPMENT COSTS</strong></TableCell>
                                </TableRow>
                                {batteryKwh > 0 && (
                                    <TableRow>
                                        <TableCell sx={{ pl: 3 }}>Battery</TableCell>
                                        <TableCell align="center">{formatNumber(batteryKw)} kW / {formatNumber(batteryKwh)} kWh</TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                <TextField
                                                    size="small"
                                                    value={formatInputNumber(capexInputs.battery.rate)}
                                                    onChange={(e) => updateCapexInput('battery', 'rate', e.target.value)}
                                                    sx={{ width: 80 }}
                                                    InputProps={{ startAdornment: '$' }}
                                                    disabled={readOnly}
                                                />
                                                <Typography variant="body2">/kWh</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <TextField
                                                size="small"
                                                value={formatInputNumber(capexInputs.battery.totalCost || 0)}
                                                onChange={(e) => updateCapexInput('battery', 'totalCost', e.target.value)}
                                                sx={{ width: 100 }}
                                                InputProps={{ startAdornment: '$' }}
                                                disabled={readOnly}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" fontWeight="medium">
                                                {formatCurrency(capexCalculations.components?.battery?.equipment || 0)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    value={itcInputs.batteryEquipment || ''}
                                                    onChange={(e) => updateItcInput('batteryEquipment', e.target.value)}
                                                    sx={{ width: 80 }}
                                                    inputProps={{ style: { textAlign: 'right' } }}
                                                    disabled={readOnly || !includeItc}
                                                />
                                                <Typography variant="body2" sx={{ ml: 0.5 }}>%</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" color="success.main">
                                                -{formatCurrency(itcCalculations.batteryEquipment?.itcAmount || 0)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" fontWeight="bold">
                                                {formatCurrency(itcCalculations.batteryEquipment?.netCost || 0)}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {generatorKw > 0 && (
                                    <TableRow>
                                        <TableCell sx={{ pl: 3 }}>Generator</TableCell>
                                        <TableCell align="center">{formatNumber(generatorKw)} kW</TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                <TextField
                                                    size="small"
                                                    value={formatInputNumber(capexInputs.generator.rate)}
                                                    onChange={(e) => updateCapexInput('generator', 'rate', e.target.value)}
                                                    sx={{ width: 80 }}
                                                    InputProps={{ startAdornment: '$' }}
                                                    disabled={readOnly}
                                                />
                                                <Typography variant="body2">/kW</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <TextField
                                                size="small"
                                                value={formatInputNumber(capexInputs.generator.totalCost || 0)}
                                                onChange={(e) => updateCapexInput('generator', 'totalCost', e.target.value)}
                                                sx={{ width: 100 }}
                                                InputProps={{ startAdornment: '$' }}
                                                disabled={readOnly}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" fontWeight="medium">
                                                {formatCurrency(capexCalculations.components?.generator?.equipment || 0)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    value={itcInputs.generatorEquipment || ''}
                                                    onChange={(e) => updateItcInput('generatorEquipment', e.target.value)}
                                                    sx={{ width: 80 }}
                                                    inputProps={{ style: { textAlign: 'right' } }}
                                                    disabled={readOnly || !includeItc}
                                                />
                                                <Typography variant="body2" sx={{ ml: 0.5 }}>%</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" color="success.main">
                                                -{formatCurrency(itcCalculations.generatorEquipment?.itcAmount || 0)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" fontWeight="bold">
                                                {formatCurrency(itcCalculations.generatorEquipment?.netCost || 0)}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}

                                {/* Equipment Subtotal */}
                                <TableRow sx={{ backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#f9f9f9' }}>
                                    <TableCell sx={{ pl: 3 }}><strong>Equipment Subtotal</strong></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell align="right"><strong>{formatCurrency(capexCalculations.equipment)}</strong></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" color="success.main" fontWeight="bold">
                                            -{formatCurrency(itcCalculations.equipmentSubtotalItc || 0)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" fontWeight="bold">
                                            {formatCurrency((capexCalculations.equipment || 0) - (itcCalculations.equipmentSubtotalItc || 0))}
                                        </Typography>
                                    </TableCell>
                                </TableRow>

                                {/* INSTALLATION COSTS */}
                                <TableRow sx={{ backgroundColor: isDark ? 'rgba(100, 150, 200, 0.1)' : '#f0f8ff' }}>
                                    <TableCell colSpan={8}><strong>INSTALLATION COSTS</strong></TableCell>
                                </TableRow>
                                {batteryKwh > 0 && (
                                    <TableRow>
                                        <TableCell sx={{ pl: 3 }}>Battery Installation</TableCell>
                                        <TableCell align="center">{formatNumber(batteryKwh)} kWh</TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                <TextField
                                                    size="small"
                                                    value={formatInputNumber(capexInputs.installation?.battery?.rate)}
                                                    onChange={(e) => updateInstallationCost('battery', 'rate', e.target.value)}
                                                    sx={{ width: 80 }}
                                                    InputProps={{ startAdornment: '$' }}
                                                    disabled={readOnly}
                                                />
                                                <Typography variant="body2">/kWh</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <TextField
                                                size="small"
                                                value={formatInputNumber(capexInputs.installation?.battery?.totalCost || 0)}
                                                onChange={(e) => updateInstallationCost('battery', 'totalCost', e.target.value)}
                                                sx={{ width: 100 }}
                                                InputProps={{ startAdornment: '$' }}
                                                disabled={readOnly}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" fontWeight="medium">
                                                {formatCurrency(capexCalculations.components?.battery?.installation || 0)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    value={itcInputs.batteryInstallation || ''}
                                                    onChange={(e) => updateItcInput('batteryInstallation', e.target.value)}
                                                    sx={{ width: 80 }}
                                                    inputProps={{ style: { textAlign: 'right' } }}
                                                    disabled={readOnly || !includeItc}
                                                />
                                                <Typography variant="body2" sx={{ ml: 0.5 }}>%</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" color="success.main">
                                                -{formatCurrency(itcCalculations.batteryInstallation?.itcAmount || 0)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" fontWeight="bold">
                                                {formatCurrency(itcCalculations.batteryInstallation?.netCost || 0)}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {generatorKw > 0 && (
                                    <TableRow>
                                        <TableCell sx={{ pl: 3 }}>Generator Installation</TableCell>
                                        <TableCell align="center">{formatNumber(generatorKw)} kW</TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                <TextField
                                                    size="small"
                                                    value={formatInputNumber(capexInputs.installation?.generator?.rate)}
                                                    onChange={(e) => updateInstallationCost('generator', 'rate', e.target.value)}
                                                    sx={{ width: 80 }}
                                                    InputProps={{ startAdornment: '$' }}
                                                    disabled={readOnly}
                                                />
                                                <Typography variant="body2">/kW</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <TextField
                                                size="small"
                                                value={formatInputNumber(capexInputs.installation?.generator?.totalCost || 0)}
                                                onChange={(e) => updateInstallationCost('generator', 'totalCost', e.target.value)}
                                                sx={{ width: 100 }}
                                                InputProps={{ startAdornment: '$' }}
                                                disabled={readOnly}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" fontWeight="medium">
                                                {formatCurrency(capexCalculations.components?.generator?.installation || 0)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    value={itcInputs.generatorInstallation || ''}
                                                    onChange={(e) => updateItcInput('generatorInstallation', e.target.value)}
                                                    sx={{ width: 80 }}
                                                    inputProps={{ style: { textAlign: 'right' } }}
                                                    disabled={readOnly || !includeItc}
                                                />
                                                <Typography variant="body2" sx={{ ml: 0.5 }}>%</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" color="success.main">
                                                -{formatCurrency(itcCalculations.generatorInstallation?.itcAmount || 0)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" fontWeight="bold">
                                                {formatCurrency(itcCalculations.generatorInstallation?.netCost || 0)}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}

                                {/* Installation Subtotal */}
                                <TableRow sx={{ backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#f9f9f9' }}>
                                    <TableCell sx={{ pl: 3 }}><strong>Installation Subtotal</strong></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell align="right"><strong>{formatCurrency(capexCalculations.installation)}</strong></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" color="success.main" fontWeight="bold">
                                            -{formatCurrency(itcCalculations.installationSubtotalItc || 0)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" fontWeight="bold">
                                            {formatCurrency((capexCalculations.installation || 0) - (itcCalculations.installationSubtotalItc || 0))}
                                        </Typography>
                                    </TableCell>
                                </TableRow>

                                {/* SOFT COSTS */}
                                <TableRow sx={{ backgroundColor: isDark ? 'rgba(100, 150, 200, 0.1)' : '#f0f8ff' }}>
                                    <TableCell colSpan={8}><strong>SOFT COSTS</strong></TableCell>
                                </TableRow>
                                {['engineering', 'permits', 'projectManagement', 'commissioning', 'contingency', 'custom'].map(key => (
                                    <TableRow key={key}>
                                        <TableCell sx={{ pl: 3 }}>
                                            {key === 'custom' ? (
                                                <TextField
                                                    size="small"
                                                    variant="standard"
                                                    placeholder="Custom Cost"
                                                    value={capexInputs.softCosts?.custom?.label || ''}
                                                    onChange={(e) => updateSoftCost('custom', 'label', e.target.value)}
                                                    disabled={readOnly}
                                                    sx={{ width: 150 }}
                                                    InputProps={{ sx: { fontSize: '0.875rem' } }}
                                                />
                                            ) : key === 'engineering' ? 'Engineering' :
                                                key === 'permits' ? 'Permits & Fees' :
                                                    key === 'projectManagement' ? 'Project Management' :
                                                        key === 'commissioning' ? 'Commissioning' : 'Contingency'}
                                        </TableCell>
                                        <TableCell align="center">—</TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                <TextField
                                                    size="small"
                                                    value={capexInputs.softCosts?.[key]?.rate}
                                                    onChange={(e) => updateSoftCost(key, 'rate', e.target.value)}
                                                    sx={{ width: 60 }}
                                                    type="number"
                                                    disabled={readOnly}
                                                />
                                                <Typography variant="body2">%</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <TextField
                                                size="small"
                                                value={formatInputNumber(capexInputs.softCosts?.[key]?.totalCost || 0)}
                                                onChange={(e) => updateSoftCost(key, 'totalCost', e.target.value)}
                                                sx={{ width: 100 }}
                                                InputProps={{ startAdornment: '$' }}
                                                disabled={readOnly}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" fontWeight="medium">
                                                {formatCurrency(capexCalculations.softCosts?.[key] || 0)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    value={itcInputs[key] || ''}
                                                    onChange={(e) => updateItcInput(key, e.target.value)}
                                                    sx={{ width: 80 }}
                                                    inputProps={{ style: { textAlign: 'right' } }}
                                                    disabled={readOnly || !includeItc}
                                                />
                                                <Typography variant="body2" sx={{ ml: 0.5 }}>%</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" color="success.main">
                                                -{formatCurrency(itcCalculations[key]?.itcAmount || 0)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" fontWeight="bold">
                                                {formatCurrency(itcCalculations[key]?.netCost || 0)}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}

                                {/* Soft Costs Subtotal */}
                                <TableRow sx={{ backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : '#f9f9f9' }}>
                                    <TableCell sx={{ pl: 3 }}><strong>Soft Costs Subtotal</strong></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell align="right"><strong>{formatCurrency(capexCalculations.softCostsTotal)}</strong></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" color="success.main" fontWeight="bold">
                                            -{formatCurrency(itcCalculations.softCostsSubtotalItc || 0)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" fontWeight="bold">
                                            {formatCurrency((capexCalculations.softCostsTotal || 0) - (itcCalculations.softCostsSubtotalItc || 0))}
                                        </Typography>
                                    </TableCell>
                                </TableRow>

                                {/* GRAND TOTAL */}
                                <TableRow sx={{ backgroundColor: isDark ? 'rgba(178, 140, 80, 0.2)' : vortxColors.gold + '20' }}>
                                    <TableCell><strong style={{ fontSize: '1.1em' }}>GRAND TOTAL</strong></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body1" fontWeight="bold">
                                            {formatCurrency(itcCalculations.totalUpfront)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Typography variant="body2" fontWeight="bold">
                                            {itcCalculations.effectiveItcRate}%
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body1" color="success.main" fontWeight="bold">
                                            -{formatCurrency(itcCalculations.totalItc)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body1" fontWeight="bold" sx={{ color: vortxColors.gold }}>
                                            {formatCurrency(itcCalculations.netCostAfterItc)}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
}

export default BudgetTab;
